import { useState } from 'react';
import { usePageMeta } from '@/lib/seo';
import { BlogCard, FeaturedBlogCard } from '@/modules/marketing/blog/BlogCard';
import { BLOG_TOPICS, POSTS } from '@/modules/marketing/blog/posts';
import type { BlogTopic } from '@/modules/marketing/blog/posts';
import { MarketingPage } from '@/modules/marketing/MarketingPage';
import { PAGES } from '@/seo/pages';

/**
 * The blog's front page.
 *
 * ## Every post is in the HTML
 *
 * The topic filter hides nothing a crawler needs. It starts on "All", which is what the build
 * renders, so the pre-rendered page links to every post; choosing a topic only changes what the
 * browser shows afterwards. A filter that rendered one topic at a time would leave the other posts
 * reachable only through JavaScript, which is the failure the pre-render exists to prevent.
 *
 * The newest post is set large above the grid. With the topic chosen, it joins the grid like any
 * other, so nothing is shown twice.
 */
export function BlogIndexPage() {
  usePageMeta(PAGES.blog);

  const [topic, setTopic] = useState<BlogTopic | 'All'>('All');
  const [featured, ...rest] = POSTS;
  const filtered = topic === 'All' ? rest : POSTS.filter((post) => post.topic === topic);
  const topics = BLOG_TOPICS.filter((name) => POSTS.some((post) => post.topic === name));

  return (
    <MarketingPage
      eyebrow="Blog"
      title="The Techie Gain Blog"
      standfirst="Practical notes on backups, monitoring, SSH, patching and server automation, written by the team that builds the platform."
      wide
    >
      <div className="mt-12 flex flex-wrap items-center justify-center gap-2" role="group" aria-label="Filter posts by topic">
        {(['All', ...topics] as const).map((name) => (
          <button
            key={name}
            type="button"
            aria-pressed={topic === name}
            onClick={() => setTopic(name)}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
              topic === name
                ? 'border-primary bg-primary/10 text-primary font-medium'
                : 'border-border/70 text-muted-foreground hover:border-primary/40 hover:text-foreground'
            }`}
          >
            {name}
          </button>
        ))}
      </div>

      {topic === 'All' && featured !== undefined && (
        <div className="mt-10">
          <FeaturedBlogCard post={featured} />
        </div>
      )}

      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((post) => (
          <BlogCard key={post.slug} post={post} />
        ))}
      </div>
    </MarketingPage>
  );
}
